import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tabs, 
  Button, 
  Table, 
  Space, 
  message, 
  Modal, 
  Tag, 
  Tooltip, 
  Select,
  DatePicker,
  Input,
  Popconfirm,
  Badge,
  Alert,
  Statistic,
  Row,
  Col,
  Radio
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CopyOutlined, 
  SettingOutlined,
  DollarOutlined,
  TeamOutlined,
  TrophyOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import SalaryTemplateForm from '../components/SalaryTemplateForm';
import EmployeeSalaryForm from '../components/EmployeeSalaryForm';

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

const SalaryManagement = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [loading, setLoading] = useState(false);
  
  // 薪资模板相关状态
  const [templates, setTemplates] = useState([]);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // 员工薪资相关状态
  const [employees, setEmployees] = useState([]);
  const [salaryModalVisible, setSalaryModalVisible] = useState(false);
  const [editingEmployeeSalary, setEditingEmployeeSalary] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // 薪资变更历史相关状态
  const [salaryChanges, setSalaryChanges] = useState([]);
  const [historyFilters, setHistoryFilters] = useState({});
  
  // 基础数据
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [salaryItems, setSalaryItems] = useState([]);
  const [stats, setStats] = useState({});
  
  // 筛选条件
  const [templateFilters, setTemplateFilters] = useState({
    department_id: undefined,
    position_id: undefined,
    is_active: undefined
  });

  useEffect(() => {
    loadBasicData();
    if (activeTab === 'templates') {
      loadTemplates();
    } else if (activeTab === 'employees') {
      loadEmployeeSalaries();
    } else if (activeTab === 'history') {
      loadSalaryChanges();
    }
  }, [activeTab]);

  const loadBasicData = async () => {
    try {
      const [departmentsRes, positionsRes, itemsRes] = await Promise.all([
        fetch('/api/departments', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/positions', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/salary/salary-items', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (departmentsRes.ok) {
        const result = await departmentsRes.json();
        setDepartments(result.data || result);
      }

      if (positionsRes.ok) {
        const result = await positionsRes.json();
        setPositions(result.data || result);
      }

      if (itemsRes.ok) {
        const result = await itemsRes.json();
        setSalaryItems(result.data);
      }
    } catch (error) {
      console.error('加载基础数据失败:', error);
    }
  };

  const loadTemplates = async (filters = templateFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.position_id) params.append('position_id', filters.position_id);
      if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
      
      const queryString = params.toString();
      const url = `/api/salary/position-templates${queryString ? '?' + queryString : ''}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const result = await response.json();
        setTemplates(result.data);
      }
    } catch (error) {
      console.error('加载薪资模板失败:', error);
      message.error('加载薪资模板失败');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeSalaries = async () => {
    setLoading(true);
    try {
      // 这里应该调用获取员工薪资列表的API
      // 暂时使用模拟数据
      setEmployees([]);
    } catch (error) {
      console.error('加载员工薪资失败:', error);
      message.error('加载员工薪资失败');
    } finally {
      setLoading(false);
    }
  };

  const loadSalaryChanges = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/salary/salary-changes', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const result = await response.json();
        setSalaryChanges(result.data.list);
      }
    } catch (error) {
      console.error('加载薪资变更历史失败:', error);
      message.error('加载薪资变更历史失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (data) => {
    try {
      const response = await fetch('/api/salary/position-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        loadTemplates();
        return true;
      } else {
        const error = await response.json();
        message.error(error.message || '创建失败');
        return false;
      }
    } catch (error) {
      console.error('创建薪资模板失败:', error);
      message.error('创建薪资模板失败');
      return false;
    }
  };

  const handleEditTemplateClick = async (templateId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/salary/position-templates/${templateId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setEditingTemplate(result.data);
        setTemplateModalVisible(true);
      } else {
        const error = await response.json();
        message.error(error.message || '获取模板详情失败');
      }
    } catch (error) {
      console.error('获取模板详情失败:', error);
      message.error('获取模板详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = async (data) => {
    try {
      const response = await fetch(`/api/salary/position-templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        loadTemplates();
        return true;
      } else {
        const error = await response.json();
        message.error(error.message || '更新失败');
        return false;
      }
    } catch (error) {
      console.error('更新薪资模板失败:', error);
      message.error('更新薪资模板失败');
      return false;
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      const response = await fetch(`/api/salary/position-templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        message.success('删除成功');
        loadTemplates();
      } else {
        const error = await response.json();
        message.error(error.message || '删除失败');
      }
    } catch (error) {
      console.error('删除薪资模板失败:', error);
      message.error('删除薪资模板失败');
    }
  };

  const handleCopyTemplate = async (id) => {
    try {
      const response = await fetch(`/api/salary/position-templates/${id}/copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        message.success('复制成功');
        loadTemplates();
      } else {
        const error = await response.json();
        message.error(error.message || '复制失败');
      }
    } catch (error) {
      console.error('复制薪资模板失败:', error);
      message.error('复制薪资模板失败');
    }
  };

  const templateColumns = [
    {
      title: '模板名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            版本 v{record.version} • {record.department_name} - {record.position_name}
          </div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <Badge 
          status={isActive ? 'success' : 'default'} 
          text={isActive ? '启用中' : '已停用'} 
        />
      ),
    },
    {
      title: '生效日期',
      dataIndex: 'effective_date',
      key: 'effective_date',
      width: 120,
    },
    {
      title: '项目数量',
      dataIndex: 'item_count',
      key: 'item_count',
      width: 100,
      render: (count) => <Tag color="blue">{count} 项</Tag>,
    },
    {
      title: '创建人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (text) => new Date(text).toLocaleDateString('zh-CN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditTemplateClick(record.id)}
            />
          </Tooltip>
          <Tooltip title="复制">
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              onClick={() => handleCopyTemplate(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个薪资模板吗？"
            onConfirm={() => handleDeleteTemplate(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const historyColumns = [
    {
      title: '员工',
      key: 'employee',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.employee_name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.employee_no} • {record.position_name}
          </div>
        </div>
      ),
    },
    {
      title: '变更类型',
      dataIndex: 'change_type',
      key: 'change_type',
      width: 120,
      render: (type) => {
        const typeMap = {
          'template_update': { text: '模板更新', color: 'blue' },
          'individual_adjustment': { text: '个人调整', color: 'green' },
          'position_change': { text: '岗位变更', color: 'orange' },
          'promotion': { text: '晋升调薪', color: 'red' }
        };
        const typeInfo = typeMap[type] || { text: type, color: 'default' };
        return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
      },
    },
    {
      title: '变更原因',
      dataIndex: 'change_reason',
      key: 'change_reason',
      ellipsis: true,
    },
    {
      title: '生效日期',
      dataIndex: 'effective_date',
      key: 'effective_date',
      width: 120,
    },
    {
      title: '审批状态',
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 100,
      render: (status) => {
        const statusMap = {
          'pending': { text: '待审批', color: 'warning' },
          'approved': { text: '已审批', color: 'success' },
          'rejected': { text: '已拒绝', color: 'error' }
        };
        const statusInfo = statusMap[status] || { text: status, color: 'default' };
        return <Badge status={statusInfo.color} text={statusInfo.text} />;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (text) => new Date(text).toLocaleDateString('zh-CN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      }),
    },
  ];

  return (
    <div style={{ padding: '10px 16px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
            <DollarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            薪资管理
          </h2>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            管理岗位薪资模板、员工个人薪资配置和薪资变更历史
          </p>
        </div>

        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="薪资模板数量"
                value={templates.length}
                prefix={<SettingOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="已配置员工"
                value={employees.length}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="薪资变更记录"
                value={salaryChanges.length}
                prefix={<HistoryOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="薪资项目类型"
                value={salaryItems.length}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'templates',
              label: '薪资模板',
              children: (
                <>
                  {/* 筛选条件 */}
                  <Card size="small" style={{ marginBottom: '16px' }}>
                    <Row gutter={16}>
                      <Col span={5}>
                        <Select
                          placeholder="筛选部门"
                          allowClear
                          style={{ width: '100%' }}
                          value={templateFilters.department_id}
                          onChange={(value) => {
                            const newFilters = { ...templateFilters, department_id: value, position_id: undefined };
                            setTemplateFilters(newFilters);
                            loadTemplates(newFilters);
                          }}
                        >
                          {departments.map(dept => (
                            <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={5}>
                        <Select
                          placeholder="筛选岗位"
                          allowClear
                          style={{ width: '100%' }}
                          value={templateFilters.position_id}
                          onChange={(value) => {
                            const newFilters = { ...templateFilters, position_id: value };
                            setTemplateFilters(newFilters);
                            loadTemplates(newFilters);
                          }}
                        >
                          {positions
                            .filter(pos => !templateFilters.department_id || pos.department_id === templateFilters.department_id)
                            .map(pos => (
                              <Option key={pos.id} value={pos.id}>{pos.name}</Option>
                            ))}
                        </Select>
                      </Col>
                      <Col span={8}>
                        <Space>
                          <span style={{ marginRight: '8px' }}>状态</span>
                          <Radio.Group
                            value={templateFilters.is_active}
                            onChange={(e) => {
                              const newFilters = { ...templateFilters, is_active: e.target.value };
                              setTemplateFilters(newFilters);
                              loadTemplates(newFilters);
                            }}
                            buttonStyle="solid"
                          >
                            <Radio.Button value={undefined}>全部</Radio.Button>
                            <Radio.Button value="true">启用</Radio.Button>
                            <Radio.Button value="false">停用</Radio.Button>
                          </Radio.Group>
                        </Space>
                      </Col>
                      <Col span={6} style={{ textAlign: 'right' }}>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setEditingTemplate(null);
                            setTemplateModalVisible(true);
                          }}
                        >
                          新建薪资模板
                        </Button>
                      </Col>
                    </Row>
                  </Card>

                  <Table
                    columns={templateColumns}
                    dataSource={templates}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      total: templates.length,
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `共 ${total} 条记录`,
                    }}
                  />
                </>
              )
            },
            {
              key: 'employees',
              label: '员工薪资',
              children: (
                <Alert
                  message="员工薪资管理"
                  description="此功能正在开发中，敬请期待。您可以在员工详情页面中配置个人薪资。"
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )
            },
            {
              key: 'history',
              label: '变更历史',
              children: (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <Space>
                      <Select placeholder="变更类型" style={{ width: 120 }} allowClear>
                        <Option value="template_update">模板更新</Option>
                        <Option value="individual_adjustment">个人调整</Option>
                        <Option value="position_change">岗位变更</Option>
                        <Option value="promotion">晋升调薪</Option>
                      </Select>
                      <Select placeholder="审批状态" style={{ width: 120 }} allowClear>
                        <Option value="pending">待审批</Option>
                        <Option value="approved">已审批</Option>
                        <Option value="rejected">已拒绝</Option>
                      </Select>
                      <RangePicker placeholder={['开始日期', '结束日期']} />
                      <Search placeholder="搜索员工" style={{ width: 200 }} />
                    </Space>
                  </div>

                  <Table
                    columns={historyColumns}
                    dataSource={salaryChanges}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      total: salaryChanges.length,
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `共 ${total} 条记录`,
                    }}
                  />
                </>
              )
            }
          ]}
        />

      </Card>

      {/* 薪资模板表单 */}
      <SalaryTemplateForm
        visible={templateModalVisible}
        onCancel={() => {
          setTemplateModalVisible(false);
          setEditingTemplate(null);
        }}
        onSubmit={editingTemplate ? handleEditTemplate : handleCreateTemplate}
        initialData={editingTemplate}
        positions={positions}
        salaryItems={salaryItems}
      />

      {/* 员工薪资表单 */}
      <EmployeeSalaryForm
        visible={salaryModalVisible}
        onCancel={() => {
          setSalaryModalVisible(false);
          setEditingEmployeeSalary(null);
          setSelectedEmployee(null);
        }}
        onSubmit={() => {}} // TODO: 实现员工薪资提交
        employee={selectedEmployee}
        templates={templates}
        salaryItems={salaryItems}
        initialData={editingEmployeeSalary}
      />
    </div>
  );
};

export default SalaryManagement;
